import type { ReactNode } from 'react';
import type { TenantStatus } from '@yourstore/domain';
import { useApi, useRead } from '../context';
import { useT } from '../../messages';
import type { IntakeDraft, PageTemplate } from '../../wire';
import { Questionnaire } from './site/Questionnaire';
import { PageCard } from './site/PageCard';
import { AddressCard } from './site/AddressCard';
import { PhotosCard } from './site/PhotosCard';
import { AnswersReview } from './site/AnswersReview';
import './site/site.css';

/** The Website tab's own two screens, beside the tab itself (`router.ts`). */
export type SitePage = 'editor' | 'answers' | null;

/**
 * **Website** — the owner's questionnaire, their page, its address and their
 * photos: four cards, each with one verdict, and two screens of their own.
 *
 *  * **The tab** (`#/b/<id>/site`): a quiet banner while the business is not
 *    live — the website is the second stage and nothing here blocks the
 *    switch-over — then the four cards.
 *  * **Their answers** (`#/b/<id>/site/answers`): one guided screen, their
 *    words → where each lands → what needs a hand, with their week, their
 *    closed days and their menu as its last sections.
 *  * **The page builder** (`#/b/<id>/site/editor`): the whole screen, drawn
 *    by the business page rather than this tab (`builder/Builder.tsx`), with
 *    its own way back here.
 *
 * The questionnaire and the page card read each other's facts — "used on the
 * page" is a page published after the owner answered, and "Start from their
 * answers" needs the answers — so the tab reads the intake and the page once
 * and hands both down. The address and the photos read their own.
 *
 * The hand-offs into the builder are client state, one document each, handed
 * once and held by the business page: the owner's answers (`onStartPage`) and
 * a template chosen on the page card (`onStartTemplate`). Opening the builder
 * plainly clears both, so it opens on the stored page (or the draft kept on
 * this computer).
 */
export function SiteTab({
  tenantId,
  name,
  tenantStatus,
  templates,
  page,
  onPage,
  onStartPage,
  onStartTemplate,
  onSeedCatalog,
  onTemplates,
}: {
  tenantId: string;
  name: string;
  tenantStatus: TenantStatus;
  /** The template list, lifted off a branding read by whichever screen made one. */
  templates: readonly PageTemplate[];
  page: SitePage;
  onPage: (page: SitePage) => void;
  onStartPage: (draft: IntakeDraft | null) => void;
  /** A template chosen here, for the builder to start from (`null` clears it). */
  onStartTemplate: (template: PageTemplate | null) => void;
  onSeedCatalog: (file: string) => void;
  onTemplates: (templates: readonly PageTemplate[]) => void;
}): ReactNode {
  const startPage = (draft: IntakeDraft): void => {
    onStartTemplate(null);
    onStartPage(draft);
    onPage('editor');
  };

  if (page === 'answers') {
    return (
      <AnswersReview
        tenantId={tenantId}
        name={name}
        tenantStatus={tenantStatus}
        templates={templates}
        onBack={() => onPage(null)}
        onStartPage={startPage}
        onSeedCatalog={onSeedCatalog}
      />
    );
  }

  return (
    <SiteOverview
      tenantId={tenantId}
      name={name}
      tenantStatus={tenantStatus}
      onTemplates={onTemplates}
      onReview={() => onPage('answers')}
      onOpenEditor={() => {
        onStartTemplate(null);
        onStartPage(null);
        onPage('editor');
      }}
      onStartPage={startPage}
      onChooseTemplate={(template) => {
        onStartPage(null);
        onStartTemplate(template);
        onPage('editor');
      }}
    />
  );
}

function SiteOverview({
  tenantId,
  name,
  tenantStatus,
  onTemplates,
  onReview,
  onOpenEditor,
  onStartPage,
  onChooseTemplate,
}: {
  tenantId: string;
  name: string;
  tenantStatus: TenantStatus;
  onTemplates: (templates: readonly PageTemplate[]) => void;
  onReview: () => void;
  onOpenEditor: () => void;
  onStartPage: (draft: IntakeDraft) => void;
  onChooseTemplate: (template: PageTemplate) => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const intake = useRead(() => api.intake(tenantId), [tenantId]);
  const branding = useRead(async () => {
    const read = await api.branding(tenantId);
    // Lifted for the answers screen's template picker: one audited read for
    // one fact, rather than a second read of the same payload there.
    onTemplates(read.contract.templates);
    return read;
  }, [tenantId]);

  const absent = intake.failure !== null && intake.failure.code === 'not_found';
  // A read keeps its last answer through a failed reload; a `404` after a
  // close is not a failure to show but the form being gone, so it wins.
  const current = absent ? null : intake.value;
  const templates = branding.value?.contract.templates ?? [];

  return (
    <div className="site">
      {/*
        "Taking bookings online" is the stage after live, and nothing on the
        console computes it yet (the header draws it as "Later"). Until the
        business is live it is certainly not reached, and this tab is the
        second stage's work — so it says so, quietly, instead of looking like
        a list of things the switch-over waits on.
      */}
      {tenantStatus === 'live' ? null : (
        <p className="site-fence" role="note">
          {t('site.fence', { name })}
        </p>
      )}
      <div className="site-grid">
        <Questionnaire
          tenantId={tenantId}
          intake={current}
          absent={absent}
          loading={intake.loading}
          failure={intake.failure}
          templates={templates}
          publishedAt={branding.value?.published_at ?? null}
          onChanged={intake.reload}
          onReview={onReview}
        />
        <PageCard
          tenantId={tenantId}
          read={branding.value}
          loading={branding.loading}
          failure={branding.failure}
          intake={current}
          onReload={branding.reload}
          onOpenEditor={onOpenEditor}
          onStartPage={onStartPage}
          onChooseTemplate={onChooseTemplate}
        />
        <AddressCard tenantId={tenantId} />
        <PhotosCard tenantId={tenantId} hasIntake={current !== null} />
      </div>
    </div>
  );
}
